/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * PublishResultsPage — /organizer/results/:raceId
 *
 * Declare, then publish. Two stages on purpose: the draft is ours to get wrong
 * and change again, and nothing unpublished can leak because an unpublished
 * winner does not exist in the award table at all.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import {
  fetchOrganizerResultsPage, fetchDeclaredResults, fetchAwards, setDeclaredPosition, publishResults,
  unpublishResults, removeResult, fetchRacePublished, ORGANIZER_RESULTS_PAGE_SIZE,
} from '../feed/results/publishApi';
import { publishBlocker, declaredWinners } from '../feed/results/publishModel';
import { formatClock, formatKm } from '../feed/results/boardModel';
import { labelOf } from '../feed/results/resultsApi';
import { idOf } from '../feed/results/boardIndexModel';
import ImportResultsPanel from './ImportResultsPanel';
import { fetchEventActivities } from '../feed/results/boardIndexApi';
import { eventOfRace, isMultiDayEvent } from '../feed/results/dailyMode';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const FLAME = '#FF6A1A';
const SOFT = '#FFF4EC';
const HAIR = 'rgba(36, 23, 8, .12)';
const DISPLAY = 'Archivo, Inter, system-ui, sans-serif';
const mono = { fontVariantNumeric: 'tabular-nums' };
const SEARCH_DELAY_MS = 300;
const numberFmt = new Intl.NumberFormat('en-IN');

const CSS = `
.prp-card{background:#fff;border:1px solid #EEE8E2;border-radius:22px;overflow:hidden;
  box-shadow:0 1px 2px rgba(36,23,8,.04),0 12px 32px -20px rgba(36,23,8,.18)}
.prp-tools{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:18px 20px;border-bottom:1px solid #EEE8E2}
.prp-search{position:relative;flex:1 1 280px;max-width:420px;display:flex;align-items:center}
.prp-search svg{position:absolute;left:14px;pointer-events:none}
.prp-search input{width:100%;min-height:44px;padding:0 14px 0 42px;border-radius:12px;box-sizing:border-box;
  border:1px solid rgba(36,23,8,.16);background:#fff;font-size:15px;color:${INK}}
.prp-search input:focus{outline:2px solid ${FLAME};outline-offset:1px;border-color:transparent}
.prp-scroll{overflow-x:auto}
.prp-table{width:100%;border-collapse:separate;border-spacing:0;min-width:760px;table-layout:fixed}
.prp-table th{position:sticky;top:0;background:#FBF8F5;text-align:left;font-size:11px;font-weight:700;letter-spacing:.08em;
  text-transform:uppercase;color:${MUTED};padding:12px 16px;border-bottom:1px solid #EEE8E2;white-space:nowrap}
.prp-table td{padding:10px 16px;border-bottom:1px solid #F3EEE9;font-size:14px;color:${INK};vertical-align:middle;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-variant-numeric:tabular-nums}
.prp-table th.num,.prp-table td.num{text-align:right}
.prp-table tbody tr:hover td{background:#FFF8F2}
.prp-table tr.marked td{background:${SOFT}}
.prp-who{display:flex;align-items:center;gap:12px;min-width:0}
.prp-who-text{display:flex;flex-direction:column;gap:4px;min-width:0}
.prp-name{font-weight:700;font-size:15px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.prp-meta{display:flex;flex-wrap:wrap;gap:6px}
.prp-table td.prp-athlete{white-space:normal;padding-top:12px;padding-bottom:12px}
.prp-ava{width:34px;height:34px;border-radius:50%;flex:none;display:inline-flex;align-items:center;justify-content:center;
  background:#F6F2EE;color:#4A3D33;font-size:12px;font-weight:800}
.prp-bib{font-size:12px;line-height:1.4;padding:2px 8px;border-radius:8px;background:#F6F2EE;color:#4A3D33;white-space:nowrap}
.prp-pos{display:inline-flex;align-items:center;justify-content:center;min-width:34px;height:30px;padding:0 6px;border-radius:9px;
  background:#F6F2EE;color:${MUTED};font-family:${DISPLAY};font-weight:800;font-size:13px;box-sizing:border-box}
.prp-pill{display:inline-block;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:800;letter-spacing:.06em}
.prp-place{min-height:38px;min-width:96px;padding:0 8px;border-radius:10px;border:1px solid rgba(36,23,8,.16);
  font-size:14px;color:${INK};background:#fff;cursor:pointer}
.prp-place.on{border-color:${FLAME};background:${SOFT};font-weight:700}
.prp-foot{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;padding:14px 20px;background:#FBF8F5}
.prp-pg{min-width:40px;height:40px;padding:0 12px;border-radius:11px;border:1px solid #E6DED6;background:#fff;color:${INK};
  font-size:14px;font-weight:700;cursor:pointer;font-variant-numeric:tabular-nums}
.prp-pg:disabled{color:#B9AA98;cursor:not-allowed}
.prp-pg.cur{background:${INK};border-color:${INK};color:#fff}
.prp-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:0 0 18px}
.prp-stat{border:1px solid #EEE8E2;border-radius:16px;padding:12px 16px;background:#fff}
.prp-stat dt{margin:0;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${MUTED}}
.prp-stat dd{margin:4px 0 0;font-family:${DISPLAY};font-size:22px;font-weight:800;color:${INK};font-variant-numeric:tabular-nums}
/* PHONES: each row becomes a card. Seven table columns cannot share a
   phone's width; squeezed, the names vanished and every figure was clipped. */
@media (max-width:720px){
  .prp-stats{grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
  .prp-stat{padding:10px 12px;border-radius:14px}
  .prp-stat dt{font-size:10px}
  .prp-stat dd{font-size:18px}
  .prp-tools{padding:14px}
  .prp-search{max-width:none;flex-basis:100%}
  .prp-scroll{overflow:visible}
  .prp-table{display:block;min-width:0}
  .prp-table colgroup,.prp-table thead{display:none}
  .prp-table tbody{display:block}
  .prp-table tr{position:relative;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
    grid-template-areas:"who who who" "dist time status" "place place action";
    gap:12px 10px;padding:14px;border-bottom:1px solid #EEE8E2}
  .prp-table tr.marked{background:${SOFT}}
  .prp-table td{display:block;padding:0;border:0;background:none !important;white-space:normal;overflow:visible}
  .prp-table td.num{text-align:left}
  .prp-table td.prp-c-pos{position:absolute;top:14px;right:14px}
  .prp-table td.prp-athlete{grid-area:who;padding:0 44px 0 0}
  .prp-table td.prp-c-dist{grid-area:dist}
  .prp-table td.prp-c-time{grid-area:time}
  .prp-table td.prp-c-status{grid-area:status}
  .prp-table td.prp-c-place{grid-area:place}
  .prp-table td.prp-c-action{grid-area:action;display:flex;align-items:center;justify-content:flex-end}
  .prp-table td[data-label]::before{content:attr(data-label);display:block;margin-bottom:3px;font-family:inherit;
    font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${MUTED}}
  .prp-place{width:100%;min-height:42px}
  .prp-foot{padding:14px;justify-content:center}
  .prp-foot > span{width:100%;text-align:center}
  .prp-pg{min-width:36px;height:38px;padding:0 10px}
}
`;

/** Status → its pill colours. */
const STATUS_STYLE = {
  FINISHED: { background: '#E7F5EC', color: '#1B7F48' },
  IN_PROGRESS: { background: '#FFF1DC', color: '#8A5300' },
  DNF: { background: '#FDECEA', color: '#B42318' },
};

function initialsOf(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || '?';
}

/** Page numbers to draw: first, last, and a window round the current one. */
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(total - 1, current + 1);
  if (from > 2) out.push(null);
  for (let n = from; n <= to; n += 1) out.push(n);
  if (to < total - 1) out.push(null);
  out.push(total);
  return out;
}

/**
 * How many places the picker offers.
 *
 * Nothing below this line enforces three: publishBlocker only refuses the same
 * place twice, and the column's own constraint is `declared_position >= 1` with
 * no ceiling. The three options were the whole limit, so raising this number is
 * the whole change — a race awarding ten deep needs no migration.
 */
const PODIUM_PLACES = 10;

/** "1st", "2nd", "3rd", "4th" … */
function ordinal(n) {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;
}

export default function PublishResultsPage() {
  const { raceId } = useParams();
  const race = Number(raceId);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  // Which row is mid-way through "Remove → Confirm". One at a time: a second
  // Remove elsewhere cancels the first, so there is never more than one armed
  // delete on screen.
  const [confirming, setConfirming] = useState(null);

  // The race's event, for its dates: a multi-day event has no file import.
  // Same cached read as the public boards.
  const boardIndex = useQuery({
    queryKey: ['board-index', 'event-activities'],
    queryFn: ({ signal }) => fetchEventActivities(apiHelper, { signal }),
    staleTime: 120000,
  });
  const raceEvent = eventOfRace(boardIndex.data, race);
  // A multi-day event's table adds each entrant's runs up (see below).
  const multiDay = isMultiDayEvent(raceEvent?.start_datetime, raceEvent?.end_datetime, raceEvent?.timezone);

  // Typing settles before it asks the server; a changed search starts at page 1.
  useEffect(() => {
    const next = search.trim();
    if (next === term) return undefined;
    const id = setTimeout(() => { setTerm(next); setPage(1); }, SEARCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [search, term]);

  // THE TABLE, ONE PAGE AT A TIME. The whole-race read this replaced was
  // capped at 500 rows by the server: a 40,000-entrant race showed 500 and had
  // no pager. Sorted (fastest first) and searched on the server.
  const results = useQuery({
    queryKey: ['organizer', 'results-page', race, multiDay, term, page],
    queryFn: ({ signal }) => fetchOrganizerResultsPage(apiHelper, {
      eventActivityId: race, page, search: term, multiDay, signal,
    }),
    // Waits for the race's dates: they decide which list this is.
    enabled: Number.isFinite(race) && !boardIndex.isPending,
    placeholderData: keepPreviousData,
  });
  // EVERY MARKED PLACE, whichever page it is on — what publishing reads.
  const declared = useQuery({
    queryKey: ['organizer', 'results-declared', race],
    queryFn: ({ signal }) => fetchDeclaredResults(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });
  const awards = useQuery({
    queryKey: ['organizer', 'awards', race],
    queryFn: ({ signal }) => fetchAwards(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });
  // Whether anything is on the public podium RIGHT NOW. Award rows outlive an
  // Unpublish by design, so they cannot answer this on their own.
  const racePublished = useQuery({
    queryKey: ['organizer', 'race-published', race],
    queryFn: ({ signal }) => fetchRacePublished(apiHelper, { eventActivityId: race, signal }),
    enabled: Number.isFinite(race),
  });

  // `rows` is THIS PAGE (the server sorts it); `marked` is every result with a
  // place, on any page. Winners, the blocker and Publish read `marked` only.
  const rows = useMemo(() => results.data?.rows || [], [results.data]);
  const marked = useMemo(() => declared.data || [], [declared.data]);
  const total = results.data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / ORGANIZER_RESULTS_PAGE_SIZE));
  const shownPage = Math.min(page, pageCount);
  const firstShown = rows.length ? (shownPage - 1) * ORGANIZER_RESULTS_PAGE_SIZE + 1 : 0;
  const lastShown = rows.length ? firstShown + rows.length - 1 : 0;
  const winners = declaredWinners(marked);
  const blocker = publishBlocker(marked);
  // HOW MANY WINNERS ARE ON THE PUBLIC PODIUM RIGHT NOW — gated on the race's
  // flag, not on the award rows alone.
  //
  // Award rows outlive an Unpublish on purpose, so counting them said
  // "3 winners published" about a race whose podium was down, and left the
  // Unpublish button live with nothing to withdraw. Found 2026-11-03 while the
  // flag was false and three award rows were still present.
  const published = racePublished.data ? (awards.data?.rows || []).length : 0;

  /**
   * The results currently ON the public podium. Remove is refused for these,
   * and THIS GUARD IS THE ONLY THING STOPPING IT.
   *
   * The obvious assumption — "an award points at the result, so the database
   * will refuse the delete" — is wrong, and was measured to be wrong on
   * 2026-11-03: `event_result` is SOFT deleted, so `deleted_at` is set, the
   * foreign key is never tested, and the award is left pointing at a row the
   * board can no longer read. The podium then draws that winner with "Time not
   * available" forever. Nothing downstream catches it.
   *
   * ANY AWARD BLOCKS IT, published or not — and that is deliberate, after
   * getting it wrong in both directions on 2026-11-03.
   *
   * The first cut blocked only while the race was published, reasoning that an
   * unpublished podium harms nobody. But `unpublishResults` KEEPS the award
   * rows (deleting them is what once made republishing impossible), so
   * removing a former winner after an Unpublish left the award pointing at a
   * soft-deleted result. Measured: three such rows. Republishing happens to
   * repair them, because publishResults rewrites the award table — but until
   * someone does, the race carries awards that reference nothing.
   *
   * Deleting the stale award instead is NOT the answer: the award table's own
   * uniqueness is still a plain constraint that counts deleted rows, so a
   * removed award would block that placing from ever being awarded again.
   * That is the same trap `event_result` just escaped, and the award table has
   * not had the conditional-index treatment yet.
   *
   * The way to remove a declared winner is therefore: clear their place,
   * publish again so the award table no longer names them, then remove.
   *
   * `idOf`, NOT String(): the fetch API hands `event_result_id` back expanded
   * as an object. `String(a.event_result_id)` yields "[object Object]", the set
   * matches nothing, and every published winner is offered a Remove button —
   * which is exactly what happened the first time this ran.
   */
  const publishedResultIds = useMemo(
    () => new Set(
      (awards.data?.rows || [])
        .map((a) => idOf(a.event_result_id))
        .filter((id) => id !== null && id !== undefined)
        .map(String),
    ),
    [awards.data],
  );

  async function mark(resultId, position) {
    setError(''); setNote('');
    setBusy(`mark-${resultId}`);
    try {
      await setDeclaredPosition(apiHelper, { resultId, position });
      await Promise.all([results.refetch(), declared.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not mark that place.');
    } finally { setBusy(''); }
  }

  /**
   * Detach one result, so the athlete can be attached again.
   *
   * This is the answer to "I picked the wrong run". There is no re-point: the
   * result goes, the entrant returns to the attach panel with every run they
   * recorded that day, and the organizer chooses again. Nothing else has to
   * change for that to work — the panel lists entrants with no result yet, so
   * the athlete reappears on the next read, and the run that was wrongly taken
   * is free again because no result holds it any more.
   */
  async function onRemove(row) {
    setError(''); setNote('');
    setBusy(`remove-${row.id}`);
    try {
      await removeResult(apiHelper, { resultId: row.id });
      setConfirming(null);
      setNote(
        `${row.athlete_name || 'That athlete'} is back on the attach panel — `
        + 'pick the right run and attach it again.',
      );
      await Promise.all([results.refetch(), declared.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not remove that result.');
    } finally { setBusy(''); }
  }

  async function onPublish() {
    setError(''); setNote('');
    setBusy('publish');
    try {
      const first = marked[0] || rows[0] || {};
      const written = await publishResults(apiHelper, {
        eventActivityId: race,
        activityName: labelOf(first.event_activity_id, `Race ${race}`),
        eventName: '',
        // Every marked place on every page — never just the page on screen.
        results: marked,
      });
      setNote(`Published ${written} winner${written === 1 ? '' : 's'}.`);
      // racePublished too: it is what gates Remove, and publishing has just
      // put these winners on the podium.
      await Promise.all([results.refetch(), declared.refetch(), awards.refetch(), racePublished.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not publish.');
    } finally { setBusy(''); }
  }

  async function onUnpublish() {
    setError(''); setNote('');
    setBusy('unpublish');
    try {
      const withdrawn = await unpublishResults(apiHelper, { eventActivityId: race });
      setNote(`Withdrawn ${withdrawn} award${withdrawn === 1 ? '' : 's'}. Your picks are kept.`);
      // The flag has just gone false, which is what lets Remove come back.
      await Promise.all([awards.refetch(), racePublished.refetch()]);
    } catch (e) {
      setError(e.message || 'Could not unpublish.');
    } finally { setBusy(''); }
  }

  if (!Number.isFinite(race)) {
    return <p style={{ padding: 24, color: MUTED }}>That race link is not valid.</p>;
  }

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '8px 4px 56px' }}>
      <Link to="/organizer/results" style={{ fontSize: 13, color: MUTED }}>← All races</Link>
      <h1 style={{ margin: '10px 0 6px', fontSize: 24, fontWeight: 700, color: INK }}>
        Declare and publish
      </h1>
      <p style={{ margin: '0 0 18px', fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
        {/* Reworded 2026-11-02, phase 7a. The old line read "Nothing is public
            until you press Publish", which was never true: a result is on the
            public board the moment it is attached, and only the PODIUM waits for
            this button. Settled as decision 2 of phase 7. */}
        Mark the places against the finishers. The results list is already public — publishing
        announces the winners, and publishing again replaces them rather than adding to them.
        {' '}
        <Link to={`/results/${race}`} style={{ color: ACCENT, fontWeight: 600 }}>View the public board</Link>
      </p>

      {/* Before the podium can be marked there have to be results to mark it
          against, so the import sits above the list rather than below it. */}
      <ImportResultsPanel
        api={apiHelper}
        eventActivityId={race}
        eventStart={raceEvent?.start_datetime}
        eventEnd={raceEvent?.end_datetime}
        eventTimezone={raceEvent?.timezone}
        existingResults={rows}
        onImported={() => results.refetch()}
      />

      {error && <div role="alert" style={{ padding: '12px 14px', borderRadius: 10, background: '#FFF1E8', color: '#8A2C06', fontSize: 14, marginBottom: 14 }}>{error}</div>}
      {note && <div role="status" style={{ padding: '12px 14px', borderRadius: 10, background: '#ECFDF3', color: '#1B7F48', fontSize: 14, marginBottom: 14 }}>{note}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <button type="button" onClick={onPublish} disabled={!!blocker || busy === 'publish'}
          style={{ minHeight: 46, padding: '0 20px', border: 0, borderRadius: 11, background: blocker ? '#D8CEC3' : INK, color: '#fff', fontWeight: 700, fontSize: 14, cursor: blocker ? 'not-allowed' : 'pointer' }}>
          {busy === 'publish' ? 'Publishing…' : 'Publish results'}
        </button>
        <button type="button" onClick={onUnpublish} disabled={published === 0 || busy === 'unpublish'}
          style={{ minHeight: 46, padding: '0 20px', borderRadius: 11, border: `1px solid ${HAIR}`, background: '#fff', color: published === 0 ? '#A69683' : INK, fontWeight: 600, fontSize: 14, cursor: published === 0 ? 'not-allowed' : 'pointer' }}>
          {busy === 'unpublish' ? 'Withdrawing…' : 'Unpublish'}
        </button>
        <span style={{ fontSize: 13, color: MUTED }}>
          {published > 0 ? `${published} winner${published === 1 ? '' : 's'} published` : 'Nothing published yet'}
          {winners.length > 0 ? ` · ${winners.length} marked` : ''}
        </span>
      </div>

      {blocker && <p style={{ fontSize: 13, color: MUTED, marginTop: -8, marginBottom: 16 }}>{blocker}</p>}

      <dl className="prp-stats">
        <div className="prp-stat"><dt>Results</dt><dd>{numberFmt.format(term ? (results.data?.total ?? 0) : total)}{term ? ' found' : ''}</dd></div>
        <div className="prp-stat"><dt>Places marked</dt><dd>{numberFmt.format(winners.length)}</dd></div>
        <div className="prp-stat"><dt>Winners published</dt><dd>{numberFmt.format(published)}</dd></div>
      </dl>

      <section className="prp-card" aria-label="Results for this race">
        <style>{CSS}</style>
        {multiDay && (
          <p role="note" style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, margin: 0, padding: '12px 20px',
            background: SOFT, borderBottom: '1px solid rgba(255,106,26,.25)', fontSize: 14, lineHeight: 1.5, color: '#4A3D33',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={FLAME} strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
              <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8v.5" />
            </svg>
            <span>
              <strong style={{ color: INK }}>Activities are added up.</strong>
              {' '}This event runs over several days, so each athlete appears once, with the distance and
              time of every activity attached to them added together — furthest first, as on the public
              board&rsquo;s &ldquo;All days&rdquo;.
            </span>
          </p>
        )}
        <div className="prp-tools">
          <div className="prp-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
            </svg>
            <label htmlFor="result-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
              Find an athlete by name or bib number
            </label>
            <input
              id="result-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find an athlete by name or bib number"
              autoComplete="off"
            />
          </div>
          <span style={{ ...mono, fontSize: 13, color: MUTED }} aria-live="polite">
            {rows.length
              ? <>Showing <strong style={{ color: INK }}>{numberFmt.format(firstShown)}–{numberFmt.format(lastShown)}</strong> of <strong style={{ color: INK }}>{numberFmt.format(total)}</strong>{term ? ' matches' : ''}</>
              : ''}
          </span>
        </div>

        {results.isPending && <p style={{ margin: 0, padding: '40px 20px', textAlign: 'center', fontSize: 14, color: MUTED }}>Loading results…</p>}
        {results.isError && !results.data && (
          <div role="alert" style={{ padding: '32px 20px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#8A2C06' }}>{results.error?.message || 'Could not load the results for this race.'}</p>
            <button type="button" className="prp-pg" onClick={() => results.refetch()}>Try again</button>
          </div>
        )}
        {!results.isPending && results.data && rows.length === 0 && (
          <p style={{ margin: 0, padding: '40px 20px', textAlign: 'center', fontSize: 14, color: MUTED }}>
            {term
              ? `No athlete here matches “${term}”.`
              : 'No results for this race yet. They appear as athletes add their runs.'}
          </p>
        )}

        {rows.length > 0 && (
          <div className="prp-scroll">
            <table className="prp-table" style={{ opacity: results.isPlaceholderData ? 0.55 : 1 }}>
              <colgroup>
                <col style={{ width: 70 }} />
                <col />
                <col style={{ width: multiDay ? 130 : 110 }} />
                <col style={{ width: multiDay ? 110 : 100 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">{term ? '#' : 'Pos'}</th>
                  <th scope="col">Athlete</th>
                  <th scope="col" className="num">{multiDay ? 'Total distance' : 'Distance'}</th>
                  <th scope="col" className="num">{multiDay ? 'Total time' : 'Time'}</th>
                  <th scope="col">Status</th>
                  <th scope="col">Place</th>
                  <th scope="col" className="num"><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
              <tr key={row.id} className={row.declared_position ? 'marked' : ''}>
                <td className="prp-c-pos"><span className="prp-pos">{numberFmt.format(firstShown + index)}</span></td>
                <td className="prp-athlete">
                  {/* Name on its own line, the tags under it and free to wrap:
                      all on one line, the bib and activity tags squeezed the
                      name out entirely and the last tag was cut off. */}
                  <span className="prp-who">
                    <span className="prp-ava" aria-hidden="true">{initialsOf(row.athlete_name)}</span>
                    <span className="prp-who-text">
                      <span className="prp-name" title={row.athlete_name || 'Athlete'}>
                        {row.athlete_name || 'Athlete'}
                      </span>
                      {(row.bib || multiDay) && (
                        <span className="prp-meta">
                          {row.bib ? <span className="prp-bib">Bib {row.bib}</span> : null}
                          {multiDay && (
                            <span className="prp-bib" style={{ background: SOFT, color: '#9A3412' }}>
                              {`${Number(row.activities) || 0} ${Number(row.activities) === 1 ? 'activity' : 'activities'} · ${Number(row.days_active) || 0} ${Number(row.days_active) === 1 ? 'day' : 'days'}`}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </span>
                </td>
                <td className="num prp-c-dist" data-label={multiDay ? 'Total distance' : 'Distance'} style={{ color: '#3B2A14' }}>{formatKm(row.distance_meters)}</td>
                <td className="num prp-c-time" data-label={multiDay ? 'Total time' : 'Time'} style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 15 }}>{formatClock(row.duration_seconds)}</td>
                <td className="prp-c-status" data-label="Status">
                  <span className="prp-pill" style={STATUS_STYLE[String(row.status || '').toUpperCase()] || { background: '#F6F2EE', color: MUTED }}>
                    {String(row.status || '—').replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="prp-c-place" data-label="Place">
                  <label htmlFor={`place-${row.id}`} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
                    Podium place for {row.athlete_name || 'this athlete'}
                  </label>
                  <select
                    id={`place-${row.id}`}
                    className={`prp-place${row.declared_position ? ' on' : ''}`}
                    value={row.declared_position ?? ''}
                    disabled={busy === `mark-${row.id}`}
                    onChange={(e) => mark(row.id, e.target.value)}
                  >
                    <option value="">—</option>
                    {Array.from({ length: PODIUM_PLACES }, (_, i) => i + 1).map((place) => (
                      <option key={place} value={place}>{ordinal(place)}</option>
                    ))}
                  </select>
                </td>

                {/* ── REMOVE, WITHDRAWN BEFORE IT SHIPPED — 2026-11-03 ──────
                    Built to answer "I attached the wrong run". It worked on
                    screen and is wrong underneath, so it is commented out
                    rather than left live.

                    WHY. `event_result.soft_delete` is TRUE, so the delete sets
                    `deleted_at` and leaves the row in place. The table's unique
                    rule on `registration_id` is a PLAIN constraint, which still
                    counts deleted rows. So a removed result goes on occupying
                    its registration for ever, and that athlete becomes:

                      · offered again by the attach panel (its query filters
                        `deleted_at IS NULL`, so the row looks gone),
                      · impossible to actually attach — the insert is refused on
                        the unique constraint, SILENTLY, inside a batch,
                      · poison to the CSV import, whose batch is one
                        transaction: one such athlete takes the whole upload
                        down. Measured: 1,400 of 10,000 rows saved, then refused.

                    MEASURED, not reasoned: after removing Kabir_patel_507, a
                    500-run attach ran to completion and registration 9500 still
                    held only the soft-deleted row. No error reached the screen.

                    There is no frontend way out. Every read available here —
                    the public table read and `organizer_race_results` alike —
                    excludes soft-deleted rows, so the row cannot be found and
                    revived, and its id cannot be learned in order to update it.

                    BOTH FIXED, 2026-11-03, and the button is live again:

                    1. `event_result`'s plain UNIQUE (registration_id) is now a
                       CONDITIONAL index — unique among rows with `deleted_at IS
                       NULL` — shipped as pack 1.0.12. Soft delete is kept, so
                       the audit trail survives, and a removed result stops
                       owning its registration.
                    2. The guard now gates on the race's `results_published`
                       flag as well as the award rows. Award rows outlive an
                       Unpublish on purpose (deleting them once made
                       republishing impossible), so guarding on their existence
                       alone blocked Remove on a race whose podium was down. */}
                <td className="num prp-c-action">
                  {publishedResultIds.has(String(row.id)) ? (
                    <span
                      style={{ fontSize: 12, color: MUTED }}
                      title={
                        racePublished.data
                          ? 'A published winner. Clear their place and publish again before removing.'
                          : 'Still named in the award table. Clear their place and publish again before removing.'
                      }
                    >
                      {racePublished.data ? 'Published' : 'Declared'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => (confirming === row.id ? onRemove(row) : setConfirming(row.id))}
                      onBlur={() => setConfirming((c) => (c === row.id ? null : c))}
                      disabled={busy === `remove-${row.id}`}
                      aria-label={
                        confirming === row.id
                          ? `Confirm removing the result for ${row.athlete_name || 'this athlete'}`
                          : `Remove the result for ${row.athlete_name || 'this athlete'}`
                      }
                      style={{
                        minHeight: 36, padding: '0 12px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                        cursor: busy === `remove-${row.id}` ? 'default' : 'pointer',
                        border: `1px solid ${confirming === row.id ? '#B91C1C' : HAIR}`,
                        background: confirming === row.id ? '#B91C1C' : '#fff',
                        color: confirming === row.id ? '#fff' : MUTED,
                      }}
                    >
                      {busy === `remove-${row.id}`
                        ? 'Removing…'
                        : (confirming === row.id ? 'Confirm' : 'Remove')}
                    </button>
                  )}
                </td>
              </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pageCount > 1 && (
          <nav className="prp-foot" aria-label="Result pages">
            <span style={{ ...mono, fontSize: 13, color: MUTED }}>
              Page <strong style={{ color: INK }}>{numberFmt.format(shownPage)}</strong> of {numberFmt.format(pageCount)}
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="prp-pg" aria-label="Previous page" disabled={shownPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Previous</button>
              {pageWindow(shownPage, pageCount).map((n, i) => (n === null
                ? <span key={`gap-${i}`} aria-hidden="true" style={{ width: 20, textAlign: 'center', color: MUTED, fontWeight: 700 }}>…</span>
                : (
                  <button
                    key={n}
                    type="button"
                    className={`prp-pg${n === shownPage ? ' cur' : ''}`}
                    aria-current={n === shownPage ? 'page' : undefined}
                    aria-label={`Page ${n}`}
                    onClick={() => setPage(n)}
                  >
                    {numberFmt.format(n)}
                  </button>
                )))}
              <button type="button" className="prp-pg" aria-label="Next page" disabled={shownPage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next ›</button>
            </div>
          </nav>
        )}
      </section>
    </div>
  );
}
